import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import {
  isArray,
  isEmpty,
  find,
  concat,
  assign,
  isNumber,
  findIndex,
  includes,
} from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Form as FormUI, DateTime, Input } from '~/components/Form';
import { t, number } from '~/helpers';
import api from '~/services/api';
import { Modal } from '~/components';
import { ONLINE } from '~/constants/default';

import * as S from './styles';

import { Bicos, Tabela, Tanques, Loading } from '..';

export default function Form({
  posto,
  tabelas,
  type,
  sending,
  visible,
  close,
}) {
  const formRef = React.useRef(null);
  const bicoRef = React.useRef(null);
  const tanqueRef = React.useRef(null);
  const loadingRef = React.useRef(null);

  const { id } = useSelector((state) => state.user.profile);

  const [tanques, setTanques] = React.useState(tabelas.tanques || []);
  const [bicos, setBicos] = React.useState(tabelas.bicos || []);

  // Title
  const titleModal = React.useMemo(() => {
    if (type === 2) return t('s.operacao.turno.abrirturno');
    if (type === 3) return t('s.operacao.turno.transferir');

    return t('s.operacao.turno.fecharturno');
  }, [type]);

  // Message success
  const messageSuccess = React.useMemo(() => {
    if (type === 2) return t('s.operacao.turno.turnoabertosucesso');
    if (type === 3) return t('s.operacao.turno.turnotransferido');

    return t('s.operacao.turno.turnofechadosucesso');
  }, [type]);

  // Validate form
  const schema = React.useMemo(
    () =>
      Yup.object().shape({
        nomeposto: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        datahorainicialturno: includes([1, 3], type)
          ? Yup.string().nullable()
          : Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        observacaoinicioturno: Yup.string().nullable(),
        datahorafinalturno:
          type === 2
            ? Yup.string().nullable()
            : Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        observacaofimturno: Yup.string().nullable(),
      }),
    [type]
  );

  // Altura tanque físico
  const handleRuleAlturaTanque = React.useCallback((value) => {
    return value.statussondatanquefisico !== ONLINE;
  }, []);

  // Callback edit tanque físico
  const handleCallbackAlturaTanque = React.useCallback(
    async (value, position, item) => {
      const { idarqueacao, idposto } = item;
      const modifyTanks = concat([], tanques);
      const input = document.getElementById(
        `input-alturatanquefisico-${position}`
      );

      try {
        const { data } = await api.get(
          `arqueacoes/${idarqueacao}/${idposto}/${value}`
        );

        modifyTanks[position].alturatanquefisico = data.altura;
        modifyTanks[position].volumetanquefisico = data.volume;
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));

        // Checks whether APi returned an error and clears the value typed in the input
        input.value = '';
        input.focus(); // Focus on input

        // Clears value if it already exists
        modifyTanks[position].alturatanquefisico = '';
        modifyTanks[position].volumetanquefisico = '';
      } finally {
        setTanques(modifyTanks);
      }
    },
    [tanques]
  );

  // Callback edit agua tanque físico
  const handleCallbackWater = React.useCallback(
    async (value, position, item) => {
      const { idarqueacao, idposto } = item;
      const modifyTanks = concat([], tanques);
      const input = document.getElementById(
        `input-alturaaguatanquefisico-${position}`
      );

      try {
        const { data } = await api.get(
          `arqueacoes/${idarqueacao}/${idposto}/${value}`
        );

        modifyTanks[position].alturaaguatanquefisico = data.altura;
        modifyTanks[position].volumeaguatanquefisico = data.volume;
        modifyTanks[position].confirmadivergenciavolume = false;
        modifyTanks[position].divergenciavolumeturnoAnterior = false;
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));

        // Checks whether APi returned an error and clears the value typed in the input
        input.value = '';
        input.focus(); // Focus on input

        // Clears value if it already exists
        modifyTanks[position].volumeaguatanquefisico = '';
        modifyTanks[position].volumeaguatanquefisico = '';
        modifyTanks[position].confirmadivergenciavolume = false;
        modifyTanks[position].divergenciavolumeturnoAnterior = false;
      } finally {
        setTanques(modifyTanks);
      }
    },
    [tanques]
  );

  // Callback edit encerrante mecanico
  const handleCallbackMecanico = React.useCallback(
    (value, index, item) => {
      const modifyBicos = concat([], bicos);

      if (item) modifyBicos[index][item] = value;
      else {
        modifyBicos[index].encerrantedigitadobico = value;
        modifyBicos[index].confirmadivergenciaencerrantebicooper = false;
        modifyBicos[index].confirmaerroatualizacaoencerrantebicooper = false;
      }

      setBicos(modifyBicos);
    },
    [bicos]
  );

  // Tabela de tanques
  const cellTanques = React.useMemo(
    () => [
      { id: 'codigotanquefisico', label: t('s.operacao.turno.tanque') },
      { id: 'nomeabreviadoproduto', label: t('s.menu.configuracao.produto') },
      {
        id: 'alturatanquefisico',
        label: t('s.operacao.turno.alturatanquefisico'),
        align: 'right',
        rule: (value) => handleRuleAlturaTanque(value),
        callback: (value, index, item) =>
          handleCallbackAlturaTanque(value, index, item),
      },
      {
        id: 'volumetanquefisico',
        label: t('s.operacao.turno.volumetanquefisico'),
        align: 'right',
        format: (value) => number(value, 1),
      },
      {
        id: 'alturaaguatanquefisico',
        label: t('s.operacao.turno.alturaaguatanquefisico'),
        align: 'right',
        rule: (value) => handleRuleAlturaTanque(value),
        callback: (value, index, item) =>
          handleCallbackWater(value, index, item),
      },
      {
        id: 'volumeaguatanquefisico',
        label: t('s.operacao.turno.volumeaguatanquefisico'),
        align: 'right',
        format: (value) => number(value, 1),
      },
    ],
    [tanques]
  );

  // Tabela de bicos
  const cellBicos = React.useMemo(
    () => [
      { id: 'idbico', label: t('s.operacao.turno.biconumero') },
      { id: 'nomeabreviadoproduto', label: t('s.menu.configuracao.produto') },
      {
        id: 'encerrantedigitadobico',
        label: t('s.operacao.turno.encerrantemecanico'),
        align: 'right',
        rule: () => true,
        callback: (value, index) => handleCallbackMecanico(value, index),
      },
    ],
    [bicos]
  );

  // Validate bicos
  const handleValidationBicos = React.useCallback(async () => {
    const errors = [];

    bicos
      .filter((bico) => bico.automaticobico === 'N')
      .map((bico) => {
        const { encerrantedigitadobico } = bico;

        if (!encerrantedigitadobico)
          errors.push(
            t('s.operacao.turno.digiteencerramento').replace('%s', bico.idbico)
          );

        return true;
      });

    if (!isEmpty(errors)) throw errors;
  }, [bicos]);

  // Validade tanques
  const handleValidationTanques = React.useCallback(async () => {
    const errors = [];

    tanques.map((tanque) => {
      const { alturatanquefisico, alturaaguatanquefisico } = tanque;

      if (!isNumber(alturatanquefisico) || !isNumber(alturaaguatanquefisico))
        errors.push(
          t('s.operacao.recebimentonf.digitealtura').replace(
            '%s',
            tanque.codigotanquefisico
          )
        );

      return true;
    });

    if (!isEmpty(errors)) throw errors;
  }, [tanques]);

  // Validade erros response
  const handleValidateErrorsResponse = React.useCallback(
    async (response) => {
      const { retorno, mensagem } = response;

      // Checks for nozzle errors
      if (!isEmpty(response.bicos)) {
        const item = response.bicos.shift();
        const index = findIndex(bicos, (bico) => bico.idbico === item.idbico);

        // Divergencia encerrante
        if (item.divergenciaencerrante)
          bicoRef.current.handleOpenModal({
            bico: bicos[index],
            error: 1,
            position: index,
          });

        // Erro atualização encerrante
        if (item.erroatualizacaoencerrante)
          bicoRef.current.handleOpenModal({
            bico: bicos[index],
            error: 2,
            position: index,
          });

        throw mensagem;
      }

      // Checks for tank errors
      if (!isEmpty(response.tanques)) {
        const item = response.tanques.shift();
        const index = findIndex(
          tanques,
          (tanque) => tanque.idtanquefisico === item.idtanquefisico
        );

        // Divergência volume
        if (item.divergenciavolume)
          tanqueRef.current.handleOpenModal({
            tanque: tanques[index],
            error: 1,
            position: index,
          });

        // Divergência volume anterior
        if (item.divergenciavolumeturnoAnterior)
          tanqueRef.current.handleOpenModal({
            tanque: tanques[index],
            error: 2,
            position: index,
          });

        throw mensagem;
      }

      if (!retorno && mensagem) throw mensagem;
    },
    [bicos, tanques]
  );

  // Submit form
  const handleSubmit = React.useCallback(
    async (data) => {
      try {
        loadingRef.current.handleOpenModal();

        await schema.validate(data, {
          abortEarly: false,
        });

        await handleValidationBicos();
        await handleValidationTanques();

        // Formar object bicos
        const serializeBicos = bicos.map((bico) => ({
          idbico: bico.idbico,
          encerrantebicooper: Number(bico.encerrantedigitadobico),
          encerranteeletronicobicooper: Number(bico.encerrantebico),
          confirmadivergenciaencerrantebicooper:
            bico.confirmadivergenciaencerrantebicooper || false,
          confirmaerroatualizacaoencerrantebicooper:
            bico.confirmaerroatualizacaoencerrantebicooper || false,
        }));

        // Format object tanques
        const serializeTanques = tanques.map((tanque) => ({
          idtanquefisico: tanque.idtanquefisico,
          codigotanquefisico: tanque.codigotanquefisico,
          alturatanquefisico: Number(tanque.alturatanquefisico),
          volumetanquefisico: Number(tanque.volumetanquefisico),
          alturaaguatanquefisico: Number(tanque.alturaaguatanquefisico),
          volumeaguatanquefisico: Number(tanque.volumeaguatanquefisico),
          confirmadivergenciavolume: tanque.confirmadivergenciavolume || false,
          status: tanque.statussondatanquefisico,
        }));

        const objectSend = {
          idturno: includes([1, 3], type) ? tabelas.idturno : 0,
          idcliente: tabelas.idcliente,
          idposto: posto.idposto,
          idusuario: id,
          datahoraturno: includes([1, 3], type)
            ? data.datahorafinalturno
            : data.datahorainicialturno,
          bicos: serializeBicos,
          tanques: serializeTanques,
          observacaoturno: includes([1, 3], type)
            ? data.observacaofimturno
            : data.observacaoinicioturno,
        };

        const response = await api.put('turnos/persiste', objectSend);

        await handleValidateErrorsResponse(response.data);
        await close();

        toast.success(messageSuccess);
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        } else if (isArray(err)) err.map((e) => toast.error(t(e)));
        else if (!isEmpty(err)) toast.error(t(err));
      } finally {
        loadingRef.current.handleCloseModal();
      }
    },
    [type, bicos, tanques, tabelas, posto, messageSuccess]
  );

  // Initial data
  const initials = React.useMemo(
    () => ({
      nomeposto: posto.nomeposto || '',
      datahorainicialturno: type === 1 ? tabelas.datahorainicialturno : null,
      observacaoinicioturno: type === 1 ? tabelas.observacaoinicioturno : null,
    }),
    [posto, tabelas, type]
  );

  // Request API Tanques
  const handleGetRequestTanques = React.useCallback(async () => {
    const params = {
      wherefield: ['idposto', 'statustanquefisico', 'tipoproduto'],
      valuewherefield: [posto.idposto, 'L', 'G'],
      typewherefield: ['EQUALS', 'EQUALS', 'NEQUALS'],
    };

    try {
      const { data } = await api.get('estoquestanquespostoscomboios', {
        params,
        paramsSerializer: (param) => qs.stringify(param),
      });

      // Verifica se não existem tanques
      if (isEmpty(tanques)) {
        setTanques(
          data.items.map((item) => ({
            idtanquefisico: item.idtanquefisico,
            codigotanquefisico: item.codigotanquefisico,
            alturatanquefisico: item.alturanivel1tanquefisico,
            volumetanquefisico: item.volume1tanquefisico,
            alturaaguatanquefisico: item.alturanivel2tanquefisico,
            volumeaguatanquefisico: item.volume2tanquefisico,
            volumecontabil: item.volume1tanquefisico,
            confirmadivergenciavolume: false,
            status: item.statustanquefisico,

            statussondatanquefisico: item.statussondatanquefisico,
            idarqueacao: item.idarqueacao,
            idposto: item.idposto,
          }))
        );
      } else {
        const modifyTanques = tanques.map((tanque) => {
          const findTanque = find(
            data.items,
            (item) => item.idtanquefisico === tanque.idtanquefisico
          );

          if (findTanque && findTanque.statussondatanquefisico === ONLINE) {
            return {
              ...tanque,
              codigotanquefisico: findTanque.codigotanquefisico,
              alturatanquefisico: findTanque.alturanivel1tanquefisico,
              volumetanquefisico: findTanque.volume1tanquefisico,
              alturaaguatanquefisico: findTanque.alturanivel2tanquefisico,
              volumeaguatanquefisico: findTanque.volume2tanquefisico,
              volumecontabil: findTanque.volume1tanquefisico,
              confirmadivergenciavolume: false,
              status: findTanque.statustanquefisico,
              statussondatanquefisico: findTanque.statussondatanquefisico,
              idarqueacao: findTanque.idarqueacao,
            };
          }

          return assign(tanque, {
            idarqueacao: findTanque.idarqueacao,
            statussondatanquefisico: findTanque.statussondatanquefisico,
          });
        });

        setTanques(modifyTanques);
      }
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, [tanques, posto]);

  // Request API Bicos
  const handleGetRequestBicos = React.useCallback(async () => {
    const params = {
      pagesize: 99999,
      wherefield: ['idposto', 'statusbico', 'tipoproduto'],
      valuewherefield: [posto.idposto, 'L', 'G'],
      typewherefield: ['EQUALS', 'EQUALS', 'NEQUALS'],
    };

    try {
      const { data } = await api.get('bicos', {
        params,
        paramsSerializer: (param) => qs.stringify(param),
      });

      if (isEmpty(bicos)) setBicos(data.items);
      else {
        const modifyBicos = bicos.map((bico) => {
          const findBico = find(
            data.items,
            (item) => item.idbico === bico.idbico
          );

          if (findBico && findBico.automaticobico === 'S') {
            return {
              ...bico,
              encerrantebico: findBico.encerrantebico,
              encerrantedigitadobico: findBico.encerrantebico,
            };
          }

          return {
            ...bico,
            automaticobico: findBico.automaticobico,
            encerrantebico: findBico.encerrantebico,
          };
        });

        setBicos(modifyBicos);
      }
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, [bicos, posto]);

  // onForm change modal bico
  const handleOnFormChangedBico = React.useCallback(
    (params) => {
      const { error, position } = params;

      if (error === 1) {
        handleCallbackMecanico(
          true,
          position,
          'confirmadivergenciaencerrantebicooper'
        );
      }

      if (error === 2)
        handleCallbackMecanico(
          true,
          position,
          'confirmaerroatualizacaoencerrantebicooper'
        );

      if (error === 3)
        handleCallbackMecanico(
          true,
          position,
          'confirmadivergenciaencerrantebicooper'
        );

      formRef.current.submitForm();
    },
    [bicos]
  );

  // onForm change modal tanque
  const handleOnFormChangedTanque = React.useCallback(
    (params) => {
      const { error, position } = params;

      const modifyTanks = concat([], tanques);

      if (error === 1) {
        modifyTanks[position].confirmadivergenciavolume = true;
      }

      if (error === 2) {
        modifyTanks[position].divergenciavolumeturnoAnterior = true;
      }

      setTanques(modifyTanks);
      formRef.current.submitForm();
    },
    [tanques]
  );

  React.useEffect(() => {
    handleGetRequestTanques();
    handleGetRequestBicos();
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      handleGetRequestTanques();
    }, 60000);

    return () => clearInterval(interval);
  }, [tanques, bicos]);

  React.useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  React.useEffect(() => {
    if (!visible) {
      setTanques([]);
      setBicos([]);
    }
  }, [visible]);

  return (
    <S.Wrapper>
      <FormUI ref={formRef} onSubmit={handleSubmit} initialData={initials}>
        <S.WrapperForm>
          <Input
            disabled
            name="nomeposto"
            label={t('s.operacao.turno.posto')}
          />

          {type !== 3 && (
            <S.WrapperGroupInput>
              <DateTime
                time
                nullable
                name="datahorainicialturno"
                label={t('s.operacao.turno.abertura')}
                disabled={type === 1}
              />

              <Input
                name="observacaoinicioturno"
                label={t('s.operacao.turno.observacao')}
                disabled={type === 1}
              />
            </S.WrapperGroupInput>
          )}

          <S.WrapperGroupInput>
            <DateTime
              time
              nullable
              name="datahorafinalturno"
              label={t('s.operacao.turno.fechamento')}
              disabled={type === 2}
            />

            <Input
              name="observacaofimturno"
              label={t('s.operacao.turno.observacao')}
              disabled={type === 2}
            />
          </S.WrapperGroupInput>
        </S.WrapperForm>

        <Tabela
          title={t('s.operacao.turno.encerrantesbicos')}
          cells={cellBicos}
          data={bicos}
          size="small"
        />

        <Tabela
          title={t('s.operacao.turno.estoquefisico')}
          cells={cellTanques}
          data={tanques}
          size="small"
        />
      </FormUI>

      <Modal
        form
        disableBackdropClick
        disableEscapeKeyDown
        ref={bicoRef}
        title={t('s.operacao.turno.encerrantesbicos')}
        component={Bicos}
        onForm={handleOnFormChangedBico}
      />

      <Modal
        form
        disableBackdropClick
        disableEscapeKeyDown
        ref={tanqueRef}
        title={t('s.operacao.turno.estoquefisico')}
        component={Tanques}
        onForm={handleOnFormChangedTanque}
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={loadingRef}
        title={titleModal}
        component={Loading}
      />
    </S.Wrapper>
  );
}

Form.propTypes = {
  posto: PropTypes.objectOf(
    PropTypes.shape({
      nomeposto: PropTypes.string,
      idposto: PropTypes.number,
      transfereturnoposto: PropTypes.number,
    })
  ),
  tabelas: PropTypes.objectOf(PropTypes.any),
  type: PropTypes.number,
  sending: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

Form.defaultProps = {
  posto: {},
  type: 0,
  tabelas: {},
};
